import { Link } from 'react-router-dom';

import { Card } from '../../components/ui/Card';
import { useCan } from '../../stores/session';
import { useRoom } from '../rooms/useRoom';

/**
 * "You are on air" - the host's own room, on the page they land on.
 *
 * ## Why it is here at all
 *
 * A live room is the one thing on this platform that is *running* while you are
 * looking at something else. Everything else waits. So the dashboard was the
 * one screen that could tell somebody their broadcast was on and did not - the
 * live rail beside it lists their room among everybody else's, which says a
 * room is live without saying it is theirs or offering the way back to it.
 *
 * ## Green, and only ever green
 *
 * The on-air red belongs to the room itself, where it means "this is live, you
 * are hearing it". Here it would read as an alarm about something that is
 * working. Green is the platform's "this is fine and running" and is the only
 * colour that does not make a working broadcast look like a problem.
 *
 * ## Asked only of people who can host
 *
 * `/me/station` answers for everybody and is null for almost all of them, so
 * this is behind `host.broadcast` rather than behind a check on the response.
 * A request per dashboard load that can only ever say "no" is a request not
 * worth making.
 *
 * It shares `['room', 'mine']` with the console, so opening the console from
 * here has the room already. And the poll it brings is the one that keeps the
 * line below honest: `useRoom` does not poll an offline room at all, so the
 * cost lands only on somebody who is actually broadcasting and is reading their
 * own now-playing while it does.
 */
export function OnAirNow() {
  const canHost = useCan('host.broadcast');
  const { data } = useRoom(undefined, { mine: true, enabled: canHost });

  const room = data?.room ?? null;

  // Not live is not a state to report. A block saying "you are off air" on the
  // page somebody visits to listen is an accusation.
  if (room === null || room.status !== 'live') return null;

  const playing = room.now_playing;
  const listeners = room.listener_count ?? 0;
  const queued = data?.queue?.length ?? 0;

  return (
    <Card tone="fresh" className="flex flex-col gap-3 p-4" data-on-air-now>
      <div className="flex items-center gap-2">
        {/*
          The same pulse the live rail uses, so "on air" looks like one thing
          across the platform rather than two similar things.
        */}
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-fresh opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-fresh" />
        </span>
        <span className="meta-caps text-fresh">You are on air</span>
        <span className="meta-caps ml-auto text-faint">
          {listeners === 1 ? '1 listening' : `${listeners} listening`}
        </span>
      </div>

      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-sm font-medium text-ink">
          {room.name ?? 'Your room'}
        </span>
        {/*
          What is on the deck, which is the thing a host away from the console
          actually wants to know - and null is a real state, not a gap: a host
          between tracks has not stopped broadcasting.
        */}
        <span className="truncate font-mono text-[11px] text-faint">
          {playing ? `${playing.title} · ${playing.artist.display_name}` : 'nothing on the deck'}
        </span>
      </div>

      <Link
        to="/console"
        data-on-air-console
        className="flex items-center justify-center rounded-[var(--radius-control)] border border-fresh-edge bg-fresh/10 px-3 py-2 text-sm font-medium text-fresh transition-colors hover:bg-fresh/20"
      >
        Back to the console
        {queued > 0 && (
          <span className="ml-2 font-mono text-[11px] opacity-70">
            {queued} queued
          </span>
        )}
      </Link>
    </Card>
  );
}
